import { useMemo, useCallback } from 'react';
import { Skeleton, Typography } from '@sunrun/experience-ui-components';
import styled from '@emotion/styled/macro';
import { Virtuoso } from 'react-virtuoso';
import { useListViewData } from 'providers/Pipeline';
import { ListViewHeader, ListViewRow } from 'components/molecules/ListViewRow';
import { ListViewControls, ListViewSortParam, Prospect } from 'types/prospect';
import { PipelineErrorDisplay } from 'components/atoms/PipelineErrorDisplay';
import { PullToRefresh } from 'components/atoms/PullToRefresh';
import {
  COLUMN_LABEL_TO_PARAM,
  PARAM_TO_COLUMN_LABEL,
} from 'constants/ListViewSortConstants';
import { getNextListViewSort } from 'helpers/listViewSortHelpers';
import { useListViewFloaterMenu } from 'hooks/useListViewFloaterMenu';
import { useListViewSortBreakpoints } from 'hooks/useListViewSortBreakpoints';

function listViewRowKey(prospect: Prospect): string {
  return prospect.prospectId || '';
}

type ListViewTabProps = {
  visible: boolean;
  listViewControls: ListViewControls;
  isListViewRoute: boolean;
  setCurrentView: (view: 'pipeline' | 'list') => void;
  onSortChange: (updates: {
    sortParam: ListViewSortParam;
    sortDirection: 'ASC' | 'DESC';
  }) => void;
  clearSearchFilters: () => void;
};

const ListViewTab = ({
  visible,
  listViewControls,
  isListViewRoute,
  setCurrentView,
  onSortChange,
  clearSearchFilters,
}: ListViewTabProps) => {
  const {
    data: listViewData,
    error: listViewDataError,
    isLoading: listViewDataLoading,
    isValidating: isListViewRefreshing,
    refresh: refreshListView,
  } = useListViewData(
    listViewControls.beginCreatedDate,
    listViewControls.endCreatedDate,
  );
  const { sortParam, sortDirection } = listViewControls;
  const sortValue = PARAM_TO_COLUMN_LABEL[sortParam];
  const { isDesktop } = useListViewSortBreakpoints();

  const filteredListViewData = useMemo(() => {
    const beginFilterDate = listViewControls.beginCreatedDate
      ? Date.parse(listViewControls.beginCreatedDate)
      : 0;
    const endFilterDate = listViewControls.endCreatedDate
      ? Date.parse(listViewControls.endCreatedDate)
      : (() => {
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          return today.getTime();
        })();

    const searchTermLower = listViewControls.searchTerm.toLowerCase();

    return listViewData.filter((prospect: Prospect) => {
      if (listViewControls.stage === 'all-leads') {
        if (!prospect.leadId) {
          return false;
        }
      } else if (listViewControls.stage === 'all-opportunities') {
        if (!prospect.opportunityId) {
          return false;
        }
      } else if (
        listViewControls.stage !== 'default' &&
        prospect.leadStage !== listViewControls.stage
      ) {
        return false;
      }
      if (listViewControls.searchTerm) {
        const name = `${prospect?.customerFirstName} ${prospect?.customerLastName}`;
        const matchesSearch =
          name.toLowerCase().includes(searchTermLower) ||
          prospect?.prospectId?.toLowerCase().includes(searchTermLower) ||
          prospect?.customerEmail?.toLowerCase().includes(searchTermLower) ||
          prospect?.customerStreet?.toLowerCase().includes(searchTermLower) ||
          prospect?.customerCity?.toLowerCase().includes(searchTermLower) ||
          prospect?.customerState?.toLowerCase().includes(searchTermLower) ||
          prospect?.customerZipCode?.toLowerCase().includes(searchTermLower) ||
          prospect?.channel?.toLowerCase().includes(searchTermLower);

        if (!matchesSearch) {
          return false;
        }
      }
      const prospectCreatedDate = Date.parse(prospect?.createdDate || '0');
      if (
        prospectCreatedDate < beginFilterDate ||
        prospectCreatedDate > endFilterDate
      ) {
        return false;
      }

      return true;
    });
  }, [listViewData, listViewControls]);

  const getStageNumber = (stage: string): number => {
    const match = stage.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : Infinity;
  };

  const sortLogic = useCallback(
    (a: Prospect, b: Prospect): number => {
      let result = 0;
      let isStringCompare = false;

      if (sortParam === 'modified') {
        result =
          Date.parse(b?.lastModifiedDate || '0') -
          Date.parse(a?.lastModifiedDate || '0');
      } else if (sortParam === 'created') {
        result =
          Date.parse(b?.createdDate || '0') - Date.parse(a?.createdDate || '0');
      } else if (sortParam === 'name') {
        const nameA = `${a?.customerLastName} ${a?.customerFirstName}`
          .trim()
          .toLowerCase();
        const nameB = `${b?.customerLastName} ${b?.customerFirstName}`
          .trim()
          .toLowerCase();
        result = nameA.localeCompare(nameB) || 0;
        isStringCompare = true;
      } else if (sortParam === 'address') {
        const addressA =
          `${a?.customerStreet} ${a?.customerCity} ${a?.customerState} ${a?.customerZipCode}`
            .trim()
            .toLowerCase();
        const addressB =
          `${b?.customerStreet} ${b?.customerCity} ${b?.customerState} ${b?.customerZipCode}`
            .trim()
            .toLowerCase();
        result = addressA.localeCompare(addressB) || 0;
        isStringCompare = true;
      } else if (sortParam === 'channel') {
        const channelA = (a?.channel || '')?.trim().toLowerCase();
        const channelB = (b?.channel || '')?.trim().toLowerCase();
        result = channelA.localeCompare(channelB) || 0;
        isStringCompare = true;
      } else if (sortParam === 'stage') {
        const stageA = (a?.leadStage || '')?.trim();
        const stageB = (b?.leadStage || '')?.trim();
        const numA = getStageNumber(stageA);
        const numB = getStageNumber(stageB);

        if (numA !== Infinity && numB !== Infinity) {
          result = numA - numB;
        } else if (numA !== Infinity) {
          result = -1;
        } else if (numB !== Infinity) {
          result = 1;
        } else {
          result = stageA.toLowerCase().localeCompare(stageB.toLowerCase());
          isStringCompare = true;
        }
      }

      if (isStringCompare) {
        return sortDirection === 'ASC' ? result : -result;
      }
      return sortDirection === 'ASC' ? -result : result;
    },
    [sortParam, sortDirection],
  );

  const sortedListViewData = useMemo(() => {
    const copy = [...filteredListViewData];
    if (sortParam) {
      copy.sort(sortLogic);
    }
    return copy;
  }, [filteredListViewData, sortParam, sortLogic]);

  const { closeFloaterMenu, isFloaterMenuOpen, onDotsClick, onRowBodyClick } =
    useListViewFloaterMenu();

  const hasActiveFilters =
    !!listViewControls.searchTerm ||
    (listViewControls.stage && listViewControls.stage !== 'default') ||
    !!listViewControls.beginCreatedDate ||
    !!listViewControls.endCreatedDate;

  if (listViewDataError && !listViewDataLoading && !listViewData?.length) {
    return (
      <div style={{ display: visible ? 'block' : 'none' }}>
        <PipelineErrorDisplay
          isListViewRoute={isListViewRoute}
          setCurrentView={setCurrentView}
          currentView="list"
        />
      </div>
    );
  }

  return (
    <ListViewPage visible={visible}>
      <ListViewContainer>
        {listViewDataLoading && sortedListViewData?.length === 0 && (
          <>
            {isDesktop && (
              <ListViewHeader
                sortValue={sortValue}
                setSortValue={() => {}}
                sortDirection={sortDirection}
              />
            )}
            {new Array(11).fill(0).map((_, index) => (
              <SkeletonWrapper key={index} />
            ))}
          </>
        )}
        {!listViewDataLoading &&
          sortedListViewData?.length === 0 &&
          (listViewData?.length > 0 || hasActiveFilters) && (
            <PipelineErrorDisplay
              isListViewRoute={isListViewRoute}
              setCurrentView={setCurrentView}
              currentView="list"
              isEmptyState={true}
              onClearFilters={() => {
                clearSearchFilters();
              }}
            />
          )}
        {!!sortedListViewData?.length && (
          <>
            {isDesktop && (
              <ListViewHeader
                sortValue={sortValue}
                sortDirection={sortDirection}
                setSortValue={(columnLabel: string) => {
                  const param = (COLUMN_LABEL_TO_PARAM[columnLabel] ??
                    '') as ListViewSortParam;
                  if (!param) {
                    return;
                  }
                  const next = getNextListViewSort(
                    sortParam,
                    sortDirection,
                    param,
                  );
                  onSortChange({
                    sortParam: next.field,
                    sortDirection: next.direction,
                  });
                }}
              />
            )}
            {!!sortedListViewData.length && (
              <PullToRefresh
                onRefresh={refreshListView}
                isRefreshing={isListViewRefreshing}>
                <Virtuoso
                  style={{
                    height: '100%',
                    width: '100%',
                  }}
                  itemContent={(index: number, prospect: Prospect) => {
                    const rowKey = listViewRowKey(prospect);
                    return (
                      <ListViewRow
                        key={rowKey}
                        prospect={prospect}
                        isEven={index % 2 === 0}
                        isFloaterMenuOpen={isFloaterMenuOpen(rowKey)}
                        onCloseFloaterMenu={closeFloaterMenu}
                        onRowBodyClick={() => onRowBodyClick(rowKey)}
                        onDotsClick={() => onDotsClick(rowKey)}
                      />
                    );
                  }}
                  data={sortedListViewData}
                />
              </PullToRefresh>
            )}
            {!listViewDataLoading &&
              !listViewData?.length &&
              !sortedListViewData.length &&
              !listViewDataError && (
                <EmptyState>
                  <Typography>
                    No prospects found for the selected filters.
                  </Typography>
                </EmptyState>
              )}
          </>
        )}
      </ListViewContainer>
    </ListViewPage>
  );
};

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100% - 164px);
  width: 100%;
`;

const SkeletonWrapper = styled(Skeleton)`
  height: 65px;
  width: calc(100% - 64px);
  margin: 0 32px 10px 32px;
  border-radius: 8px;
  box-sizing: border-box;

  @media (max-width: 879px) {
    width: calc(100% - 32px);
    margin: 0 16px 10px 16px;
  }

  @media (max-width: 560px) {
    width: 100%;
    margin: 0 0 10px 0;
    border-radius: 0;
  }
`;

const ListViewPage = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  overflow-x: hidden;
`;

const ListViewContainer = styled.div`
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;

  @media (min-width: 880px) {
    overflow-x: auto;
  }
`;

export { ListViewTab };
